import type {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
} from 'axios'
import type { ErrorMessage } from '@/error/error'

import { BrowserAuthError } from '@azure/msal-browser'
import { ElLoading, ElMessage } from 'element-plus'

import { TIMEOUT_MILISECONDS, BFF_HTTP_BASE_URL } from '@/settings/httpSetting'
import { TOKEN_REFRESH_LEAD } from '@/settings/config'
import { ONE_SECOND } from '@/settings/constants'
import axios from 'axios'
import { errorMessages } from '@/error/error'
import { storageKeys, useStorage } from '@/hooks/core/useStorage'
import { b2cPolicies } from '@/utils/auth/policies'
import { msalConfig, msalInstance } from '@/utils/auth/authConfig'
import { authorizatedInitiate } from '@/components/CommonAuthorizatedInitiate'
import { errorMessage } from '@/utils/util'
import { getMessage } from '@/utils/message'
import { sleep } from '@/utils'
import { ApiResponseStatusEnum } from '@/enums'

/** @description 進行中のリクエストをカウント */
let requestCount: number = 0
/** @description ロード画面 */
let loading = null as any
/** @description メッセージクリアロック */
let clearMessageLock: boolean = false

export const bffHTTP: AxiosInstance = axios.create({
  baseURL: BFF_HTTP_BASE_URL,
  timeout: TIMEOUT_MILISECONDS,
  withCredentials: true, // cors 対応
  headers: { 'Content-Type': 'application/json' },

  // 200 以上の status を interceptor の error handler ではなく response handler に転送させる
  validateStatus: (status: number): boolean => {
    return status >= ApiResponseStatusEnum.SUCCESSFUL_STATUS
  },
})

interface ExcutingRequest {
  url: string
  method: string
}

let excutingRequestList: ExcutingRequest[] = []

bffHTTP.interceptors.request.use(async (config: AxiosRequestConfig<any>) => {
  const controller = new AbortController()
  config.signal = controller.signal

  controlHTTPMessage()

  const alreadyExist = checkRequestExist(config)
  if (alreadyExist) {
    controller.abort()
    return config
  }

  createLoading(config)
  setNoLoadingAPI(config)

  if (!config.headers) {
    controller.abort()
    return config
  }

  try {
    await updateToken()
  } catch (error) {
    handleUpdateTokenError(error)
    controller.abort()
    return config
  }

  // 更新後のトークンをヘッダーに設定
  setCommonHeader(config)

  return config
})

bffHTTP.interceptors.response.use(
  (response: AxiosResponse<any, any>): AxiosResponse<any, any> => {
    deleteFromRequestExistList(response.config)
    handle504Response(response.status)

    // response status が 200 以外の場合はローディングの保持は不要
    response.status === ApiResponseStatusEnum.SUCCESSFUL_STATUS
      ? handleCloseLoading(response)
      : closeLoadingHard()

    return response
  },

  async (error: AxiosError): Promise<never> => {
    if (error.message.includes('canceled')) {
      closeLoadingHard()
      return Promise.reject(error)
    }

    deleteFromRequestExistList(error.config)

    if (error.message.includes('timeout')) {
      errorMessage(getMessage('EB0003'), 'error')
    }

    if (error.code === 'ECONNABORTED') {
      error.name =
        errorMessages.find((item: ErrorMessage) => item.message === error.message)
          ?.code ?? ''
    }

    errorMessage(getMessage('EF9905'), 'error')
    closeLoadingHard()

    return Promise.reject(error)
  }
)

function controlHTTPMessage(): void {
  if (!clearMessageLock) {
    ElMessage.closeAll()
    clearMessageLock = true
    setTimeout(() => (clearMessageLock = false), 5000)
  }
}

/** 行118～135: @see Bug 2433: CDPF-187 送信するボタンをEnterキーで押下すると処理時に内部エラーが発生する場合がある*/
/** reuqestが実行中（loading中）の場合、画面でEnterキー押下による同じreuqestの呼び出しを無効にするように。 */
function checkRequestExist(config: AxiosRequestConfig<any>): boolean {
  const alreadyExist = excutingRequestList.some((request: any) => {
    return request.url === config.url && request.method === config.method
  })

  if (alreadyExist) {
    return true
  } else {
    excutingRequestList.push({
      url: config.url ?? '',
      method: config.method ?? '',
    })

    return false
  }
}

function deleteFromRequestExistList(config: AxiosRequestConfig<any>) {
  excutingRequestList = excutingRequestList.filter(
    (item) => item.url !== config.url && item.method !== config.method
  )
}

function createLoading(config: AxiosRequestConfig<any>) {
  //ロード画面必要の場合のみ
  if (!config.params?.notCreateLodding) {
    // 既に進行中のリクエストが存在する場合はロード画面が既に作成済み
    if (!requestCount) {
      loading = ElLoading.service({
        lock: true,
        text: 'Loading',
        background: 'rgba(0, 0, 0, 0.3)',
      })
    }

    requestCount++
  }
}

function setNoLoadingAPI(config: AxiosRequestConfig<any>): void {
  const apis = [
    '/transactions/receive_count',
    '/transactions/send_count',
    '/carts/1/count',
  ]

  const isTarget = config.url ? apis.includes(config.url) : false
  isTarget && loading.close()
}

async function updateToken(): Promise<void> {
  const { loadUserInformation, loadAccount } = useStorage()
  const refreshDeadline: number = loadUserInformation().exp * ONE_SECOND
  const shouldRefreshToken: boolean = Date.now() + TOKEN_REFRESH_LEAD > refreshDeadline

  if (shouldRefreshToken) {
    const authority =
      sessionStorage.getItem('fromLoginOrSignUp') === 'SignUp'
        ? b2cPolicies.authorities.signUp.authority
        : b2cPolicies.authorities.signIn.authority

    const tokenResponse = await msalInstance.acquireTokenSilent({
      scopes: ['openid'],
      authority: authority ?? '',
      forceRefresh: false,
      account: loadAccount(),
      redirectUri: msalConfig.auth.redirectUri,
    })

    await authorizatedInitiate(tokenResponse.idToken, true)

    sessionStorage.setItem(storageKeys.ID_TOKEN, tokenResponse.idToken)

    sessionStorage.setItem(
      storageKeys.CONFIG_HEADER_AUTHORIZATION,
      `Bearer ${tokenResponse.idToken}`
    )
  }
}

function handleUpdateTokenError(error: unknown): void {
  if (error instanceof BrowserAuthError) {
    sessionStorage.clear()
    localStorage.clear()
    msalInstance.loginRedirect()
  }

  console.error('getIDTokenError', error)
}

function setCommonHeader(config: AxiosRequestConfig): void {
  const headers = config.headers as AxiosRequestHeaders
  const { loadUserLoginInformation } = useStorage()
  const loginInformation = loadUserLoginInformation()

  if (loginInformation && !config.params?.useCustomCorporationInfo) {
    // 企業 ID
    headers.corporate_id = loginInformation.corporate_id
    // 法人番号
    headers.corporate_number = loginInformation.corporate_number
    // 利用者 ID
    headers.user_id = loginInformation.user_id
    // ロールコード
    headers.role_code = loginInformation.role_code
  }

  headers.Authorization = sessionStorage.getItem(
    storageKeys.CONFIG_HEADER_AUTHORIZATION
  ) as string
}

function handle504Response(status: number): void {
  if (status === 504) {
    errorMessage(getMessage('EB0003'), 'error')
    closeLoadingHard()
  }
}

function handleCloseLoading(response: AxiosResponse<any, any>): void {
  requestCount--

  // 後続的なリクエストが存在する場合、最後のレスポンスが戻されるまでロード画面を維持
  const holdLoading: boolean | undefined = response.config.params?.holdLoading

  if (!requestCount && !holdLoading) {
    /**
     * - 一部リクエストの処理が早く、ロード画面が表示されずに終わるので、一定時間フリーズする
     *
     * - ElLoading の service 式作成は Singleton を作成するので、
     *   close 前に service を再び作成しても同じインスタンスが返されて close のタイミングで閉じてしまう。
     *
     * - sleep が非同期なので sleep 後に新しい ElLoading を作成したい場合は
     *   その作成を setTimeout で遅延させる必要がある（遅延の時間の入力は必要ない）
     */
    sleep(100).then(() => loading.close())
  }
}

function closeLoadingHard(): void {
  requestCount--
  sleep(500).then(() => loading.close())
}
